<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'HelpCenter',
  },
})

// pages/help/onboarding.vue — Module 24 (§9.10). Halaman "grup paling
// penting" — checklist interaktif urutan setup wajib. Urutan di sini
// BUKAN saran UX, tapi dipaksa foreign key di database (kalau dibalik,
// form akan error karena dropdown Position/Unit kosong) — lihat
// App\Support\OnboardingSteps di backend, sumber data yang sama persis
// yang dibaca <OnboardingChecklist />.
</script>

<template>
  <div>
    <div class="d-flex align-center gap-2 mb-4">
      <RouterLink
        :to="{ name: 'help' }"
        class="text-decoration-none text-medium-emphasis text-body-2"
      >
        Bantuan
      </RouterLink>
      <VIcon
        icon="ri-arrow-right-s-line"
        size="16"
        class="text-medium-emphasis"
      />
      <span class="text-body-2">Onboarding &amp; Urutan Setup Awal</span>
    </div>

    <div class="d-flex align-center gap-4 mb-4">
      <VAvatar
        color="primary"
        variant="tonal"
        size="64"
        rounded="lg"
      >
        <VIcon
          icon="ri-flag-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Onboarding &amp; Urutan Setup Awal
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Langkah wajib sebelum QUALIVA siap dipakai operasional sehari-hari
        </p>
      </div>
    </div>

    <VAlert
      type="warning"
      variant="tonal"
      icon="ri-error-warning-line"
      class="mb-6"
    >
      <strong>Ini bukan sekadar saran urutan pengerjaan</strong> — ini urutan yang
      <strong>dipaksa oleh relasi data (foreign key)</strong> di database. Kalau Anda
      membalik urutannya (misalnya mengisi Pegawai sebelum Jabatan), form akan gagal karena
      dropdown yang dibutuhkan masih kosong. Contoh paling sering ditanyakan: <em>"kenapa dropdown
        Jabatan di form Pegawai kosong?"</em> — jawabannya karena Master Data Jabatan belum diisi
      dulu (lihat langkah 4 di bawah).
    </VAlert>

    <VCard class="mb-6">
      <VCardTitle class="pa-5 pb-2">
        <VIcon
          icon="ri-list-check-3"
          class="me-2"
          color="primary"
        />
        Checklist Interaktif
      </VCardTitle>
      <VCardText class="pa-5 pt-3">
        <p class="text-body-2 mb-4">
          Centang setiap langkah yang sudah selesai dikerjakan. Progress ini tersimpan
          <strong>untuk seluruh instalasi</strong> (bukan cuma untuk akun Anda) — kalau ada admin
          lain yang login belakangan, mereka akan langsung tahu langkah mana yang sudah/belum
          dikerjakan.
        </p>
        <OnboardingChecklist />
      </VCardText>
    </VCard>

    <VCard>
      <VCardTitle class="pa-5 pb-2">
        <VIcon
          icon="ri-question-line"
          class="me-2"
          color="warning"
        />
        Kenapa Urutannya Harus Begini?
      </VCardTitle>
      <VCardText class="pa-5 pt-3">
        <VTable>
          <thead>
            <tr>
              <th style="min-width:60px">
                #
              </th>
              <th style="min-width:220px">
                Langkah
              </th>
              <th>Kenapa Urutan Ini Penting</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1-2</td>
              <td><strong>Lisensi &amp; Role Default</strong></td>
              <td class="text-body-2 py-2">
                Tanpa lisensi aktif, seluruh fitur domain (Dokumen, Checklist, dst.) diblokir. Role
                default (super-admin, direktur, dst.) biasanya sudah otomatis ter-seed saat instalasi.
              </td>
            </tr>
            <tr>
              <td>3</td>
              <td><strong>Unit sebelum yang lain</strong></td>
              <td class="text-body-2 py-2">
                Hampir semua entity lain (Pegawai, Alat, Insiden, dst.) punya kolom "Unit" yang
                dipilih lewat dropdown. Kalau Unit kosong, dropdown itu kosong juga.
              </td>
            </tr>
            <tr>
              <td>4</td>
              <td><strong>Jabatan sebelum Pegawai</strong></td>
              <td class="text-body-2 py-2">
                Form Pegawai mewajibkan memilih Jabatan. Ini yang paling sering bikin bingung
                pengguna baru — kalau Jabatan belum diisi, tidak ada satu pun pilihan di dropdown
                Jabatan saat membuat Pegawai baru.
              </td>
            </tr>
            <tr>
              <td>5-7</td>
              <td><strong>Pegawai, lalu Master Data pendukung lain</strong></td>
              <td class="text-body-2 py-2">
                Vendor/Alat/Fasilitas bebas urutan satu sama lain, tapi harus ada sebelum dipakai
                modul MFK/Peningkatan Mutu. Kategori Dokumen/Checklist/Assessment/Indikator dan Jenis
                Insiden harus ada sebelum Anda mulai membuat Dokumen/Template — dropdown kategorinya
                akan kosong kalau belum diisi.
              </td>
            </tr>
            <tr>
              <td>8</td>
              <td><strong>Akun User untuk Pegawai</strong></td>
              <td class="text-body-2 py-2">
                Data Pegawai (Master Data) dan akun login (User) adalah dua hal terpisah di QUALIVA.
                Pegawai yang sudah terdata belum tentu bisa login — perlu dibuatkan akun User dan
                diberi Role secara terpisah.
              </td>
            </tr>
            <tr>
              <td>9</td>
              <td><strong>Approval Flow sebelum staf mulai bekerja</strong></td>
              <td class="text-body-2 py-2">
                Kalau staf sudah mulai mengajukan dokumen untuk direview sebelum Approval Flow-nya
                diatur, tombol "Ajukan Review" akan gagal karena sistem tidak tahu siapa yang harus
                menyetujui.
              </td>
            </tr>
            <tr>
              <td>10-11</td>
              <td><strong>Setting &amp; Operasional Harian</strong></td>
              <td class="text-body-2 py-2">
                Setelah semua fondasi di atas siap, barulah QUALIVA benar-benar siap dipakai sehari-hari
                oleh seluruh staf.
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>
  </div>
</template>
